// pages/TeacherTestSender.js
// ─────────────────────────────────────────────────────────────
// Teacher / Parent fills a form and sends a WhatsApp test link.
// POST /createTestSession → returns wa.me link → opens WhatsApp.
//
// Features added (2026-04-07):
//  • Home button (top-left)
//  • Information button (top-right) — explains page & usage
//  • Dynamic Chapter/Unit distribution (like MockExam page):
//      After selecting Grade + Subject + Medium, click "Load Chapters"
//      to fetch available chapters/units from the API, then assign
//      how many questions from each chapter/unit.
// ─────────────────────────────────────────────────────────────
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { BASE_API_URL, API_HEADERS } from "./config";

const GRADES   = ["NEET", "Class 12", "Class 11", "Class 10"];
const SUBJECTS = ["Biology", "Physics", "Chemistry", "Mathematics", "Zoology", "Botany","உயிரியல்"];
const MEDIUMS  = ["English", "Tamil"];
const TIME_OPTIONS = [15, 30, 45, 60];

// ── Styles ────────────────────────────────────────────────────
const S = {
  page: {
    minHeight: "100vh",
    background: "#f0f4f8",
    fontFamily: "'Segoe UI', sans-serif",
    padding: "0 0 48px 0",
  },
  topBar: {
    background: "#1a237e",
    color: "#fff",
    padding: "14px 20px",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  topTitle: { fontSize: "17px", fontWeight: "700" },
  topBtnHome: {
    padding: "7px 14px",
    borderRadius: "8px",
    border: "none",
    background: "rgba(255,255,255,0.18)",
    color: "#fff",
    fontWeight: "700",
    fontSize: "13px",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    gap: "5px",
  },
  topBtnInfo: {
    padding: "7px 14px",
    borderRadius: "8px",
    border: "none",
    background: "rgba(255,255,255,0.18)",
    color: "#fff",
    fontWeight: "700",
    fontSize: "13px",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    gap: "5px",
  },
  center: {
    display: "flex",
    justifyContent: "center",
    padding: "32px 16px",
  },
  card: {
    background: "#fff",
    borderRadius: "16px",
    boxShadow: "0 4px 24px rgba(0,0,0,0.10)",
    padding: "32px 28px",
    width: "100%",
    maxWidth: "520px",
  },
  header: {
    fontSize: "20px",
    fontWeight: "700",
    color: "#1a237e",
    marginBottom: "4px",
  },
  sub: {
    fontSize: "13px",
    color: "#888",
    marginBottom: "24px",
  },
  label: {
    display: "block",
    fontSize: "13px",
    fontWeight: "600",
    color: "#444",
    marginBottom: "5px",
    marginTop: "14px",
  },
  input: {
    width: "100%",
    padding: "10px 12px",
    borderRadius: "8px",
    border: "1.5px solid #ddd",
    fontSize: "14px",
    outline: "none",
    boxSizing: "border-box",
    transition: "border 0.2s",
  },
  select: {
    width: "100%",
    padding: "10px 12px",
    borderRadius: "8px",
    border: "1.5px solid #ddd",
    fontSize: "14px",
    background: "#fff",
    boxSizing: "border-box",
  },
  row2: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: "12px",
  },
  btnSend: {
    marginTop: "24px",
    width: "100%",
    padding: "14px",
    background: "#25D366",
    color: "#fff",
    border: "none",
    borderRadius: "10px",
    fontSize: "16px",
    fontWeight: "700",
    cursor: "pointer",
    letterSpacing: "0.3px",
  },
  btnCopy: {
    marginTop: "10px",
    width: "100%",
    padding: "11px",
    background: "#e8eaf6",
    color: "#3949ab",
    border: "none",
    borderRadius: "10px",
    fontSize: "14px",
    fontWeight: "600",
    cursor: "pointer",
  },
  btnLoadChapters: {
    marginTop: "14px",
    width: "100%",
    padding: "11px",
    background: "#e8eaf6",
    color: "#1a237e",
    border: "1.5px solid #c5cae9",
    borderRadius: "10px",
    fontSize: "14px",
    fontWeight: "700",
    cursor: "pointer",
  },
  resultBox: {
    marginTop: "20px",
    background: "#e8f5e9",
    border: "1px solid #a5d6a7",
    borderRadius: "10px",
    padding: "14px 16px",
    fontSize: "13px",
    color: "#2e7d32",
    wordBreak: "break-all",
  },
  error: {
    marginTop: "14px",
    background: "#ffebee",
    border: "1px solid #ef9a9a",
    borderRadius: "8px",
    padding: "10px 14px",
    color: "#c62828",
    fontSize: "13px",
  },
  loading: {
    textAlign: "center",
    color: "#888",
    marginTop: "18px",
    fontSize: "14px",
  },
  divider: {
    borderTop: "1.5px solid #e8eaf6",
    margin: "20px 0",
  },
  sectionTitle: {
    fontSize: "13px",
    fontWeight: "700",
    color: "#3949ab",
    textTransform: "uppercase",
    letterSpacing: "0.05em",
    marginBottom: "8px",
  },
  chapterBox: {
    background: "#f5f7ff",
    border: "1.5px solid #c5cae9",
    borderRadius: "12px",
    padding: "14px",
    marginBottom: "8px",
  },
  chapterTitle: {
    fontSize: "13px",
    fontWeight: "800",
    color: "#1a237e",
    marginBottom: "8px",
  },
  unitRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: "6px",
    gap: "10px",
  },
  unitLabel: {
    fontSize: "13px",
    color: "#444",
    flex: 1,
  },
  unitInput: {
    width: "70px",
    padding: "6px 10px",
    borderRadius: "8px",
    border: "1.5px solid #ddd",
    fontSize: "14px",
    fontWeight: "700",
    textAlign: "center",
    outline: "none",
    background: "#fff",
  },
  distBadge: (ok) => ({
    display: "inline-block",
    fontSize: "12px",
    fontWeight: "700",
    padding: "3px 10px",
    borderRadius: "20px",
    background: ok ? "#e8f5e9" : "#ffebee",
    color: ok ? "#2e7d32" : "#c62828",
    border: "1px solid " + (ok ? "#a5d6a7" : "#ef9a9a"),
    marginLeft: "8px",
  }),
  // ── Info Modal ───────────────────────────────────────────────
  overlay: {
    position: "fixed",
    inset: 0,
    zIndex: 1000,
    background: "rgba(0,0,0,0.65)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "24px 16px",
  },
  modal: {
    background: "#fff",
    borderRadius: "20px",
    boxShadow: "0 8px 40px rgba(0,0,0,0.20)",
    padding: "32px 28px",
    width: "100%",
    maxWidth: "480px",
    position: "relative",
  },
  modalClose: {
    position: "absolute",
    top: "14px",
    right: "16px",
    background: "none",
    border: "1.5px solid #ddd",
    borderRadius: "8px",
    width: "32px",
    height: "32px",
    fontSize: "16px",
    cursor: "pointer",
    color: "#888",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  infoTitle: {
    fontSize: "20px",
    fontWeight: "800",
    color: "#1a237e",
    marginBottom: "6px",
  },
  infoSub: {
    fontSize: "13px",
    color: "#888",
    marginBottom: "20px",
  },
  infoSection: {
    marginBottom: "16px",
  },
  infoSectionTitle: {
    fontSize: "13px",
    fontWeight: "700",
    color: "#3949ab",
    textTransform: "uppercase",
    letterSpacing: "0.05em",
    marginBottom: "6px",
  },
  infoText: {
    fontSize: "14px",
    color: "#444",
    lineHeight: "1.6",
  },
  infoBanner: {
    background: "#e8f5e9",
    border: "1.5px solid #a5d6a7",
    borderRadius: "10px",
    padding: "12px 16px",
    fontSize: "14px",
    color: "#2e7d32",
    fontWeight: "600",
    marginBottom: "16px",
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  infoOptionRow: {
    display: "flex",
    alignItems: "flex-start",
    gap: "10px",
    marginBottom: "8px",
    fontSize: "14px",
    color: "#333",
  },
  infoOptionIcon: {
    fontSize: "18px",
    minWidth: "22px",
  },
};

// ── Information Modal ─────────────────────────────────────────
function InfoModal({ onClose }) {
  return (
    <div style={S.overlay} onClick={onClose}>
      <div style={S.modal} onClick={function(e){ e.stopPropagation(); }}>
        <button style={S.modalClose} onClick={onClose}>✕</button>

        <div style={S.infoTitle}>ℹ️ How This Works</div>
        <div style={S.infoSub}>Send Test Link — Guide for Parents & Teachers</div>

        {/* Who can use */}
        <div style={S.infoBanner}>
          👨‍👩‍👧 This page can be used by both <strong>&nbsp;Parents&nbsp;</strong> and <strong>&nbsp;Teachers</strong>
        </div>

        {/* What does it do */}
        <div style={S.infoSection}>
          <div style={S.infoSectionTitle}>📌 What does this page do?</div>
          <div style={S.infoText}>
            Fill in the form below to create a personalized mock test for a student.
            Once you submit, WhatsApp will open automatically with a ready-to-send test link.
            The student taps the link, enters their name, and begins the test immediately.
          </div>
        </div>

        {/* Form options */}
        <div style={S.infoSection}>
          <div style={S.infoSectionTitle}>⚙️ Available Options</div>
          <div style={S.infoOptionRow}>
            <span style={S.infoOptionIcon}>🎓</span>
            <span><strong>Grade / Exam Level</strong> — Choose from NEET, Class 10, 11, or 12.</span>
          </div>
          <div style={S.infoOptionRow}>
            <span style={S.infoOptionIcon}>📘</span>
            <span><strong>Subject</strong> — Biology, Physics, Chemistry, Mathematics, Zoology, or Botany.</span>
          </div>
          <div style={S.infoOptionRow}>
            <span style={S.infoOptionIcon}>🌐</span>
            <span><strong>Medium</strong> — English or Tamil.</span>
          </div>
          <div style={S.infoOptionRow}>
            <span style={S.infoOptionIcon}>📖</span>
            <span><strong>Chapter / Unit distribution</strong> — After selecting Grade, Subject &amp; Medium, click <em>"Load Chapters"</em> to see all available chapters and units. You can assign how many questions to pick from each chapter/unit.</span>
          </div>
          <div style={S.infoOptionRow}>
            <span style={S.infoOptionIcon}>🔢</span>
            <span><strong>No. of Questions</strong> — Total number of questions in the test.</span>
          </div>
          <div style={S.infoOptionRow}>
            <span style={S.infoOptionIcon}>⏱️</span>
            <span><strong>Time Limit</strong> — 15, 30, 45, or 60 minutes.</span>
          </div>
        </div>

        {/* Link info */}
        <div style={S.infoSection}>
          <div style={S.infoSectionTitle}>🔗 About the test link</div>
          <div style={S.infoText}>
            The test link is generated securely and expires after use or after a set duration.
            Results are automatically sent to the email you provide.
            You can also copy the link and share it via any messaging app.
          </div>
        </div>

        <button
          style={{ ...S.btnSend, marginTop: "8px" }}
          onClick={onClose}
        >
          Got it! Let's go 🚀
        </button>
      </div>
    </div>
  );
}

// ── Main Component ────────────────────────────────────────────
export default function TeacherTestSender() {
  var navigate = useNavigate();

  const [form, setForm] = useState({
    teacher_phone:   "",
    teacher_email:   "",
    student_phone:   "",
    grade_level:     "NEET",
    subject:         "Biology",
    medium:          "English",
    num_questions:   20,
    time_limit_mins: 30,
  });

  // ── Chapter/Unit distribution state ───────────────────────
  const [chaptersMap,  setChaptersMap]  = useState(null);  // { chapter: Set(units) }
  const [chapDist,     setChapDist]     = useState({});    // { chapter: { unit: "N" } }
  const [chapLoading,  setChapLoading]  = useState(false);
  const [chapError,    setChapError]    = useState("");
  const [allQuestions, setAllQuestions] = useState([]);    // raw questions from API

  // ── Send state ─────────────────────────────────────────────
  const [loading, setLoading] = useState(false);
  const [result,  setResult]  = useState(null);
  const [error,   setError]   = useState("");
  const [copied,  setCopied]  = useState(false);

  // ── Info modal state ───────────────────────────────────────
  const [showInfo, setShowInfo] = useState(false);

  // ── Form field change ──────────────────────────────────────
  function handleChange(e) {
    var key = e.target.name;
    var val = e.target.value;
    if (key === "num_questions" || key === "time_limit_mins") {
      val = parseInt(val, 10);
    }
    setForm(function(prev) { return Object.assign({}, prev, { [key]: val }); });
    // Reset chapter data if grade/subject/medium changes
    if (key === "grade_level" || key === "subject" || key === "medium") {
      setChaptersMap(null);
      setChapDist({});
      setChapError("");
      setAllQuestions([]);
    }
  }

  // ── Load chapters from API ─────────────────────────────────
  async function handleLoadChapters() {
    setChapLoading(true);
    setChapError("");
    setChaptersMap(null);
    setChapDist({});

    try {
      var url = BASE_API_URL + "/dynamiconeword"
        + "?grade_level=" + encodeURIComponent(form.grade_level)
        + "&subject="     + encodeURIComponent(form.subject)
        + "&medium="      + encodeURIComponent(form.medium);

      var res  = await fetch(url, { headers: API_HEADERS });
      var data = await res.json();

      if (!res.ok || !Array.isArray(data) || data.length === 0) {
        setChapError("No questions found for the selected Grade, Subject & Medium. Try a different combination.");
        setChapLoading(false);
        return;
      }

      // Build chaptersMap
      var cmap = {};
      data.forEach(function(q) {
        var ch = q.chapter || "General";
        if (!cmap[ch]) cmap[ch] = new Set();
        if (q.unit) cmap[ch].add(q.unit);
      });

      // Build initial dist
      var dist = {};
      Object.keys(cmap).forEach(function(ch) {
        dist[ch] = {};
        var units = Array.from(cmap[ch]);
        if (units.length === 0) {
          dist[ch]["__all__"] = "";
        } else {
          units.forEach(function(u) { dist[ch][u] = ""; });
        }
      });

      setAllQuestions(data);
      setChaptersMap(cmap);
      setChapDist(dist);

    } catch (err) {
      setChapError("Network error: " + err.message);
    }

    setChapLoading(false);
  }

  // ── Update distribution input ─────────────────────────────
  function updateDist(ch, unit, val) {
    setChapDist(function(prev) {
      var next = Object.assign({}, prev);
      next[ch] = Object.assign({}, prev[ch]);
      next[ch][unit] = val;
      return next;
    });
  }

  // ── Sum of all dist inputs ────────────────────────────────
  function distTotal() {
    var sum = 0;
    Object.values(chapDist).forEach(function(units) {
      Object.values(units).forEach(function(v) { sum += parseInt(v, 10) || 0; });
    });
    return sum;
  }

  // ── Build chapter/unit strings for backend ────────────────
  function buildChapterUnitPayload() {
    // If no distribution set, return empty (server picks random)
    if (!chaptersMap) return { chapter: form.chapter || "", unit: form.unit || "" };

    var dt = distTotal();
    if (dt === 0) {
      // No distribution entered — pass empty, server will do random
      return { chapter: "", unit: "" };
    }

    // Build distribution JSON for backend
    // We pass it as a stringified distribution map
    var distMap = {};
    Object.keys(chapDist).forEach(function(ch) {
      Object.keys(chapDist[ch]).forEach(function(u) {
        var n = parseInt(chapDist[ch][u], 10) || 0;
        if (n > 0) {
          if (!distMap[ch]) distMap[ch] = {};
          distMap[ch][u] = n;
        }
      });
    });

    // For simple API compat: if only 1 chapter selected, pass it as chapter param
    var chKeys = Object.keys(distMap);
    if (chKeys.length === 1) {
      var ch = chKeys[0];
      var uKeys = Object.keys(distMap[ch]);
      if (uKeys.length === 1 && uKeys[0] !== "__all__") {
        return { chapter: ch, unit: uKeys[0], dist_json: JSON.stringify(distMap) };
      }
      return { chapter: ch, unit: "", dist_json: JSON.stringify(distMap) };
    }

    return { chapter: "", unit: "", dist_json: JSON.stringify(distMap) };
  }

  // ── Send test ─────────────────────────────────────────────
  async function handleSend(e) {
    e.preventDefault();
    setError("");
    setResult(null);

    if (!form.student_phone || form.student_phone.length < 10) {
      setError("Please enter a valid student WhatsApp number (with country code, e.g. 91XXXXXXXXXX).");
      return;
    }
    if (!form.teacher_email.includes("@")) {
      setError("Please enter your email address so the result is sent to you.");
      return;
    }

    // Validate distribution if set
    if (chaptersMap) {
      var dt = distTotal();
      if (dt > 0 && dt !== form.num_questions) {
        setError(
          "Distributed questions (" + dt + ") must equal total No. of Questions (" +
          form.num_questions + "). Please adjust the chapter distribution or leave all blank for random selection."
        );
        return;
      }
    }

    setLoading(true);

    var chapUnitPayload = buildChapterUnitPayload();
    var payload = Object.assign({}, form, chapUnitPayload);

    try {
      var res = await fetch(BASE_API_URL + "/createTestSession", {
        method:  "POST",
        headers: API_HEADERS,
        body:    JSON.stringify(payload),
      });

      var data = await res.json();

      if (!res.ok) {
        setError(data.error || "Failed to create session. Please try again.");
        setLoading(false);
        return;
      }

      setResult(data);
      window.open(data.whatsapp_link, "_blank");

    } catch (err) {
      setError("Network error: " + err.message);
    }

    setLoading(false);
  }

  function handleCopyLink() {
    if (!result) return;
    navigator.clipboard.writeText(result.test_url).then(function() {
      setCopied(true);
      setTimeout(function() { setCopied(false); }, 2000);
    });
  }

  // ── Render ────────────────────────────────────────────────
  var chapList = chaptersMap ? Object.keys(chaptersMap) : [];
  var dt       = distTotal();
  var distOk   = dt === form.num_questions;

  return (
    <div style={S.page}>

      {/* ── Top Bar ── */}
      <div style={S.topBar}>
        <button style={S.topBtnHome} onClick={function() { navigate("/"); }}>
          🏠 Home
        </button>
        <div style={S.topTitle}>📤 Send Mock Test</div>
        <button style={S.topBtnInfo} onClick={function() { setShowInfo(true); }}>
          ℹ️ Info
        </button>
      </div>

      {/* ── Info Modal ── */}
      {showInfo && <InfoModal onClose={function() { setShowInfo(false); }} />}

      {/* ── Form Card ── */}
      <div style={S.center}>
        <div style={S.card}>

          <div style={S.header}>📤 Send Mock Test</div>
          <div style={S.sub}>
            Fill the details below — WhatsApp will open with the test link ready to send.<br/>
            <span style={{ color: "#3949ab", fontWeight: "600" }}>
              Can be used by Parents or Teachers 👨‍👩‍👧‍👦
            </span>
          </div>

          <form onSubmit={handleSend}>

            {/* ── Teacher / Parent Contact ── */}
            <div style={S.sectionTitle}>Your Details</div>

            <label style={S.label}>Your WhatsApp Number (with country code)</label>
            <input
              style={S.input}
              type="tel"
              name="teacher_phone"
              placeholder="e.g. 919876543210"
              value={form.teacher_phone}
              onChange={handleChange}
              required
            />

            <label style={S.label}>Your Email (to receive result)</label>
            <input
              style={S.input}
              type="email"
              name="teacher_email"
              placeholder="teacher@school.com or parent@gmail.com"
              value={form.teacher_email}
              onChange={handleChange}
              required
            />

            {/* ── Student ── */}
            <div style={{ ...S.divider }} />
            <div style={S.sectionTitle}>Student Details</div>

            <label style={S.label}>Student WhatsApp Number (with country code)</label>
            <input
              style={S.input}
              type="tel"
              name="student_phone"
              placeholder="e.g. 919876543211"
              value={form.student_phone}
              onChange={handleChange}
              required
            />

            {/* ── Test Parameters ── */}
            <div style={{ ...S.divider }} />
            <div style={S.sectionTitle}>Test Parameters</div>

            <label style={S.label}>Grade / Exam Level</label>
            <select style={S.select} name="grade_level" value={form.grade_level} onChange={handleChange}>
              {GRADES.map(function(g) { return <option key={g} value={g}>{g}</option>; })}
            </select>

            <label style={S.label}>Subject</label>
            <select style={S.select} name="subject" value={form.subject} onChange={handleChange}>
              {SUBJECTS.map(function(s) { return <option key={s} value={s}>{s}</option>; })}
            </select>

            <label style={S.label}>Medium</label>
            <select style={S.select} name="medium" value={form.medium} onChange={handleChange}>
              {MEDIUMS.map(function(m) { return <option key={m} value={m}>{m}</option>; })}
            </select>

            <div style={S.row2}>
              <div>
                <label style={S.label}>No. of Questions</label>
                <input
                  style={S.input}
                  type="number"
                  name="num_questions"
                  min="5"
                  max="100"
                  value={form.num_questions}
                  onChange={handleChange}
                />
              </div>
              <div>
                <label style={S.label}>Time Limit (mins)</label>
                <select style={S.select} name="time_limit_mins" value={form.time_limit_mins} onChange={handleChange}>
                  {TIME_OPTIONS.map(function(t) { return <option key={t} value={t}>{t} min</option>; })}
                </select>
              </div>
            </div>

            {/* ── Chapter / Unit Distribution ── */}
            <div style={{ ...S.divider }} />
            <div style={S.sectionTitle}>
              Chapter / Unit Distribution
              {chaptersMap && dt > 0 && (
                <span style={S.distBadge(distOk)}>
                  {dt} / {form.num_questions} assigned
                </span>
              )}
            </div>

            {!chaptersMap && (
              <div style={{ fontSize: "13px", color: "#888", marginBottom: "8px" }}>
                Select Grade, Subject &amp; Medium above, then click the button below
                to see available chapters and set how many questions to pick from each.
                Leave blank to pick questions randomly.
              </div>
            )}

            <button
              type="button"
              style={S.btnLoadChapters}
              onClick={handleLoadChapters}
              disabled={chapLoading}
            >
              {chapLoading
                ? "⏳ Loading chapters..."
                : chaptersMap
                  ? "🔄 Reload Chapters (" + form.grade_level + " · " + form.subject + " · " + form.medium + ")"
                  : "📖 Load Chapters & Units"}
            </button>

            {chapError && (
              <div style={{ ...S.error, marginTop: "10px" }}>⚠️ {chapError}</div>
            )}

            {/* Chapter distribution boxes */}
            {chaptersMap && chapList.length > 0 && (
              <div style={{ marginTop: "14px" }}>
                <div style={{ fontSize: "12px", color: "#888", marginBottom: "10px" }}>
                  Enter how many questions to pick from each chapter/unit. Leave blank (or 0) to skip.
                  Total assigned must equal <strong>{form.num_questions}</strong>, or leave all blank for random.
                </div>

                <div style={{ maxHeight: "320px", overflowY: "auto", paddingRight: "4px" }}>
                  {chapList.map(function(ch) {
                    var unitKeys = Object.keys(chapDist[ch] || {});
                    var chapTotal = unitKeys.reduce(function(sum, u) {
                      return sum + (parseInt(chapDist[ch][u], 10) || 0);
                    }, 0);
                    return (
                      <div key={ch} style={S.chapterBox}>
                        <div style={S.chapterTitle}>
                          📖 {ch}
                          {chapTotal > 0 && (
                            <span style={{
                              marginLeft: "8px",
                              fontSize: "11px",
                              background: "#e8eaf6",
                              color: "#3949ab",
                              padding: "1px 8px",
                              borderRadius: "20px",
                              fontWeight: "600",
                            }}>
                              {chapTotal} Q
                            </span>
                          )}
                        </div>
                        {unitKeys.map(function(u) {
                          return (
                            <div key={u} style={S.unitRow}>
                              <span style={S.unitLabel}>
                                {u === "__all__" ? "All questions" : "↳ " + u}
                              </span>
                              <input
                                type="number"
                                min="0"
                                placeholder="0"
                                style={S.unitInput}
                                value={chapDist[ch][u]}
                                onChange={function(e) { updateDist(ch, u, e.target.value); }}
                              />
                            </div>
                          );
                        })}
                      </div>
                    );
                  })}
                </div>

                {dt > 0 && !distOk && (
                  <div style={{ ...S.error, marginTop: "8px" }}>
                    ⚠️ You have assigned <strong>{dt}</strong> questions but the total is set to <strong>{form.num_questions}</strong>.
                    Please make them equal or leave all fields blank for random selection.
                  </div>
                )}
                {dt > 0 && distOk && (
                  <div style={{
                    marginTop: "8px",
                    background: "#e8f5e9",
                    border: "1px solid #a5d6a7",
                    borderRadius: "8px",
                    padding: "8px 14px",
                    fontSize: "13px",
                    color: "#2e7d32",
                    fontWeight: "600",
                  }}>
                    ✅ Distribution set! {dt} questions across selected chapters.
                  </div>
                )}
              </div>
            )}

            {/* ── Send Button ── */}
            <button style={S.btnSend} type="submit" disabled={loading}>
              {loading ? "Creating..." : "📲 Open WhatsApp & Send Test"}
            </button>

          </form>

          {/* ── Error ── */}
          {error && <div style={S.error}>⚠️ {error}</div>}

          {/* ── Success ── */}
          {result && (
            <div>
              <div style={S.resultBox}>
                ✅ Session created!<br />
                <strong>Test Link:</strong><br />
                {result.test_url}<br /><br />
                <strong>Expires:</strong> {new Date(result.expires_at).toLocaleString()}
              </div>
              <button style={S.btnCopy} onClick={handleCopyLink}>
                {copied ? "✅ Copied!" : "📋 Copy Test Link"}
              </button>
            </div>
          )}

        </div>
      </div>
    </div>
  );
}
